import { Publish } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Button, InputLabel, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';
import { useAtomValue } from 'jotai';
import { FC, SyntheticEvent, useCallback } from 'react';
import { activeUserOptionsAtom } from '../../../../../services/state/AppConfigStateJ';
import { Circle } from '../../../../DealFlow2/components/DealModal/DealAddModalContent/Circle';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import { objectiveInitialValues } from './objectiveInitialValues';
import { objectiveSchema } from './objectiveSchema';
import {
  FieldContainer,
  FormGrid,
  FormikOption,
  formSubtitleSx,
  SubFormContainer,
} from './StyledFormComponents';

export const ButtonContainer = styled('div')`
  margin-top: 1.5rem;
  display: flex;
  gap: 1rem;
`;

export interface ObjectiveFormValues {
  id?: number;
  name: string;
  categoryId?: number;
  ownerId: number | null;
}

export interface ObjectiveFormProps {
  onClose: () => void;
  initialValues?: ObjectiveFormValues;
  onCancel?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (values: ObjectiveFormValues) => void | Promise<any>;
}

export const ObjectiveForm: FC<ObjectiveFormProps> = ({ onClose, initialValues, onSubmit, onCancel }) => {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  if (!companyId) {
    throw new Error('companyId is undefined');
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<ObjectiveFormValues>({
    defaultValues: initialValues ?? objectiveInitialValues,
    mode: 'all',
    resolver: yupResolver(objectiveSchema as ObjectSchema<ObjectiveFormValues>),
  });

  const userOptions = useAtomValue(activeUserOptionsAtom);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  }, [onClose, onCancel]);

  const submit = handleSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <SubFormContainer>
      <Typography component='h6' variant='body1' sx={{ ...formSubtitleSx }}>
        <Circle isOpen={true}>
          <Typography variant='body1' color={'white'}>
            {1}
          </Typography>
        </Circle>
        Objective
      </Typography>
      <FieldContainer>
        <FormGrid>
          <InputLabel htmlFor='name'>Name</InputLabel>
          <Controller
            name='name'
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                id='name'
                name={field.name}
                error={Boolean(fieldState.error && fieldState.isTouched)}
                helperText={fieldState.error && fieldState.isTouched ? fieldState.error.message : ''}
                onBlur={field.onBlur}
                value={field.value}
                onChange={field.onChange}
                placeholder='Enter Name'
              />
            )}
          />

          <InputLabel htmlFor='ownerId'>Owner</InputLabel>
          <Controller
            name='ownerId'
            control={control}
            render={({ field, fieldState }) => (
              <Autocomplete
                options={userOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name='ownerId'
                    error={Boolean(fieldState.error && fieldState.isTouched)}
                    helperText={fieldState.error && fieldState.isTouched ? fieldState.error.message : ''}
                    placeholder='Select Owner '
                  />
                )}
                onChange={(_e: SyntheticEvent, value: FormikOption | null) =>
                  field.onChange(value ? Number(value.id) : null)
                }
                onBlur={field.onBlur}
                value={userOptions.find((option) => option.id === field.value) ?? null}
                selectOnFocus
              />
            )}
          />
          {/* empty div for proper grid layout */}
          <div></div>
          <ButtonContainer>
            <LoadingButton
              variant='contained'
              color={'secondary'}
              size={'large'}
              disabled={!isDirty || Object.keys(errors).length !== 0 || isSubmitting}
              onClick={submit}
              loading={isSubmitting}
              endIcon={<Publish />}
              loadingPosition='end'
            >
              {initialValues ? 'Save Changes' : 'Save and Next'}
            </LoadingButton>
            <Button type='button' variant='outlined' color='secondary' size={'large'} onClick={handleCancel}>
              Cancel
            </Button>
          </ButtonContainer>
        </FormGrid>
      </FieldContainer>
    </SubFormContainer>
  );
};
